import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getDashboardData } from '../../../models/dashboardModel';
import NetworkError from '../../../components/common/NetworkError';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Import dashboard components
import ProcurementDashboard from './ProcurementDashboard';
import CRMDashboard from './CRMDashboard';
import ITDashboard from './ITDashboard';
import HODDashboard from './HODDashboard';
import StandardDashboard from './StandardDashboard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  ChartDataLabels
);

function DashboardMain() {
  const { user, originalUser, can } = useAuth();
  const [dashboardType, setDashboardType] = useState('standard');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState('network');
  const [isRetrying, setIsRetrying] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);

  // Function to fetch dashboard data
  const fetchDashboardData = async (isRetry = false) => {
    try {
      if (isRetry) {
        setIsRetrying(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const result = await getDashboardData();
      
      if (result.success) {
        setDashboardData(result.data);
        setDashboardType(result.dashboardType);
      } else {
        setError(result.error || 'Failed to load dashboard data');
        setErrorType(result.errorType || 'server');
      }
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      // Determine if this is a network error
      const errorMessage = err.message || 'Error loading dashboard data';
      setError(errorMessage);
      setErrorType('network');
    } finally {
      setLoading(false);
      setIsRetrying(false);
    }
  };

  // Retry function
  const handleRetry = () => {
    fetchDashboardData(true);
  };

  // Load dashboard data from API
  useEffect(() => {
    // Only fetch if user is available
    if (user) {
      fetchDashboardData();
    }
  }, [user?.user_id, originalUser?.user_id]); // Re-fetch when user changes (including masquerading)

  if (loading) {
    return (
      <div className="p-4 py-6 md:p-8 space-y-6 md:space-y-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <NetworkError 
        error={error}
        errorType={errorType}
        onRetry={handleRetry}
        isRetrying={isRetrying}
        title="Dashboard Error"
      />
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-4 py-6 md:p-8 space-y-6 md:space-y-8">
        <div className="text-center text-gray-500">No dashboard data available</div>
      </div>
    );
  }

  // Render appropriate dashboard based on dashboard type
  const renderDashboard = () => {
    switch (dashboardType) {
      case 'procurement':
        return <ProcurementDashboard user={user} dashboardData={dashboardData} />;
      case 'crm':
        return <CRMDashboard user={user} dashboardData={dashboardData} />;
      case 'it':
        return <ITDashboard user={user} dashboardData={dashboardData} />;
      case 'hod':
        return <HODDashboard user={user} dashboardData={dashboardData} />;
      case 'standard':
      default:
        return <StandardDashboard user={user} dashboardData={dashboardData} />;
    }
  };

  // Early return for standard dashboard (special case with different wrapper)
  if (dashboardType === 'standard') {
    return (
      <div className="p-4 py-6 md:p-8 space-y-6 md:space-y-8">
        <StandardDashboard user={user} dashboardData={dashboardData} />
      </div>
    );
  }

  return (
    <div className="p-4 py-6 md:p-8 space-y-6 md:space-y-8">

      {renderDashboard()}

    </div>
  );
}

export default DashboardMain;
